// popcorn-shell L1 — xterm.js terminal UX for the in-tab Elixir REPL.
//
// This runs in the WebOTP page (webotp.html) and is driven from webotp_boot.js,
// which pumps the VM's fd 1 into it and its submitted lines back into stdin.
//
// It owns a real xterm.js Terminal + a hand-rolled readline (line buffer, cursor,
// history, ctrl+W/U/A/E, wrapped-line aware redraw). Tab-completion round-trips
// ASYNChronously through Elixir (WebOTP.Repl's completion frame), so we can't use
// a readline lib that expects a synchronous completion callback — hence hand-rolled.
//
// Wiring contract with webotp_boot.js (all optional):
//   PopcornTerm.mount()                 create + open the terminal (idempotent)
//   PopcornTerm.setNode("tab_ab12")     prompt node id (cyan `iex(<node>)>`)
//   PopcornTerm.onSubmit = (line) => …  fired on Enter (writes the line to stdin)
//   PopcornTerm.onComplete = (line) =>… fired on Tab   (asks the VM to complete it)
//   PopcornTerm.writeOutput(text,isErr) print an eval result (red when isErr) + new prompt
//   PopcornTerm.writeCompletions(items) print a completion list, keep current line
//   PopcornTerm.writeLine(text,color)   print an unsolicited line (peer waves, etc.)
//   PopcornTerm.writeRaw(text)          stream raw IO.puts/write bytes (the VM's fd 1)
//   PopcornTerm.dump()                  visible-buffer text (for CDP verification)
//   PopcornTerm.submit(line)            programmatic submit (hidden #repl-run compat)

import { Terminal } from "@xterm/xterm";
import { FitAddon } from "@xterm/addon-fit";
import "@xterm/xterm/css/xterm.css";

const ESC = "\x1b";
const CYAN = ESC + "[36m";
const RED = ESC + "[31m";
const DIM = ESC + "[90m";
const RESET = ESC + "[0m";

class PopcornTerminal {
  constructor() {
    this.term = null;
    this.fit = null;
    this.node = "this";
    this._buf = "";
    this._cursor = 0; // index into _buf
    this._renderedCursor = 0; // where the physical cursor currently sits
    this._hist = [];
    this._hi = -1; // history nav position; -1 = editing a fresh line
    this._stash = ""; // in-progress line stashed while navigating history
    this._pending = false; // awaiting an eval result — input is frozen
    this._cont = false; // mid-block: the VM said the submission is incomplete
    this._pasteQueue = []; // whole lines from a paste, waiting their turn
    this._pasteTail = null; // a paste's trailing partial line, inserted last
    this._promptLen = this._plainPrompt().length;
    this.onSubmit = () => {};
    this.onComplete = () => {};
  }

  // Mid-block the prompt changes to `...`, exactly as IEx does — and it is the
  // SAME WIDTH as the normal one, which is not cosmetic: `_promptLen` is the
  // origin for every cursor calculation in `_paint`, so a prompt that changed
  // width mid-line would misplace the cursor on wrapped input.
  _plainPrompt() {
    return (this._cont ? "..." : "iex") + "(" + this.node + ")> ";
  }
  _ansiPrompt() {
    return CYAN + this._plainPrompt() + RESET;
  }

  setNode(node) {
    this.node = String(node || "this").replace(/@.*/, "");
    this._promptLen = this._plainPrompt().length;
  }

  mount() {
    if (this.term) {
      this.fit && this.fit.fit();
      this.term.focus();
      return;
    }
    const container = document.getElementById("term");
    if (!container) return;

    this.term = new Terminal({
      cursorBlink: true,
      convertEol: false,
      fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
      fontSize: this._fontSize(),
      scrollback: 2000,
      theme: {
        background: "#0d1117",
        foreground: "#c9d1d9",
        cursor: "#c9d1d9",
        cyan: "#39c5cf",
        brightCyan: "#56d4dd",
        red: "#ff6a69",
        brightRed: "#ff8785",
        brightBlack: "#6e7681",
      },
    });
    this.fit = new FitAddon();
    this.term.loadAddon(this.fit);
    this.term.open(container);
    this._safeFit();
    this.term.focus();

    // Tap-to-focus (mobile): tapping anywhere on the terminal focuses input.
    container.addEventListener("pointerdown", () => this.term.focus());

    this.term.onData((d) => this._onData(d));

    // Keep cols correct on rotate/resize; keep the prompt above the mobile
    // keyboard by shrinking to the visible viewport when it appears.
    const relayout = () => this._relayout();
    window.addEventListener("resize", relayout);
    window.addEventListener("orientationchange", relayout);
    if (window.visualViewport) {
      window.visualViewport.addEventListener("resize", relayout);
      window.visualViewport.addEventListener("scroll", relayout);
    }
    this._relayout();
  }

  _fontSize() {
    const w = window.innerWidth || 400;
    if (w < 360) return 12;
    if (w < 480) return 13;
    return 14;
  }

  _safeFit() {
    try {
      this.fit && this.fit.fit();
    } catch (_) {}
  }

  // Resize the terminal to fit the width, and cap its height to what's visible
  // above the on-screen keyboard so the active prompt is never occluded.
  _relayout() {
    const container = document.getElementById("term");
    if (!container || !this.term) return;
    const fs = this._fontSize();
    if (this.term.options.fontSize !== fs) this.term.options.fontSize = fs;

    const vv = window.visualViewport;
    if (vv) {
      const top = container.getBoundingClientRect().top;
      // space between the terminal's top and the bottom of the visible viewport
      const avail = vv.height - top - 12;
      const h = Math.max(140, Math.min(360, Math.floor(avail)));
      container.style.height = h + "px";
    }
    this._safeFit();
  }

  // ---- readline ----------------------------------------------------------

  _onData(d) {
    if (this._pending) return; // frozen while an eval is in flight
    switch (d) {
      case "\r": // Enter
      case "\n":
        return this._enter();
      case "\x7f": // Backspace / DEL
      case "\b":
        return this._backspace();
      case "\t": // Tab -> async completion
        return this.onComplete(this._buf);
      case "\x01": // Ctrl+A -> home
      case ESC + "[H":
        return this._home();
      case "\x05": // Ctrl+E -> end
      case ESC + "[F":
        return this._end();
      case "\x15": // Ctrl+U -> kill to line start
        return this._killToStart();
      case "\x0b": // Ctrl+K -> kill to line end
        return this._killToEnd();
      case "\x17": // Ctrl+W -> delete word before cursor
        return this._deleteWord();
      case "\x03": // Ctrl+C -> cancel current line
        return this._cancel();
      case "\x04": // Ctrl+D -> ignored (nothing to exit into)
        return;
      case "\x0c": // Ctrl+L -> clear screen, keep line
        return this._clearScreen();
      case ESC + "[D": // Left
        return this._left();
      case ESC + "[C": // Right
        return this._right();
      case ESC + "[A": // Up -> history prev
        return this._histPrev();
      case ESC + "[B": // Down -> history next
        return this._histNext();
      case ESC + "[3~": // Delete (forward)
        return this._deleteForward();
      default:
        // A multi-line paste does NOT arrive as repeated "\r" cases: xterm hands
        // the whole clipboard over in one onData call with the newlines inside
        // it (and normalised to \r by its own paste helper). Falling through to
        // the control-char strip below would delete those newlines WITHOUT a
        // separator, silently turning a pasted module into
        // `defmodule Foo do  def hello, do: "world"end`. So split first.
        if (d && /[\r\n]/.test(d)) return this._paste(d);
        // printable text (single keypress or a paste); strip control chars
        if (d && d.charCodeAt(0) >= 0x20) {
          const clean = d.replace(/[\x00-\x1f\x7f]/g, "");
          if (clean) this._insert(clean);
        }
    }
  }

  // Queue a paste rather than submitting it in one go. Each line has to wait for
  // the VM to answer the one before it — the answer is what tells us whether the
  // next prompt is `iex(` or `...(`, and the readline is frozen until it lands.
  // Text after the final newline is left UNSUBMITTED in the input, which is what
  // a real terminal does with a paste that does not end in a newline.
  _paste(text) {
    const lines = String(text).replace(/\r\n?/g, "\n").split("\n");
    this._pasteTail = lines.pop();
    this._pasteQueue.push(...lines);
    this._drainPaste();
  }

  _drainPaste() {
    if (this._pending) return;
    if (this._pasteQueue.length) {
      this._setLine(this._pasteQueue.shift().replace(/[\x00-\x1f\x7f]/g, ""));
      this._enter();
      return;
    }
    if (this._pasteTail !== null) {
      const tail = this._pasteTail.replace(/[\x00-\x1f\x7f]/g, "");
      this._pasteTail = null;
      if (tail) this._insert(tail);
    }
  }

  _insert(s) {
    this._buf = this._buf.slice(0, this._cursor) + s + this._buf.slice(this._cursor);
    this._cursor += s.length;
    this._render();
  }

  _backspace() {
    if (this._cursor === 0) return;
    this._buf = this._buf.slice(0, this._cursor - 1) + this._buf.slice(this._cursor);
    this._cursor--;
    this._render();
  }

  _deleteForward() {
    if (this._cursor >= this._buf.length) return;
    this._buf = this._buf.slice(0, this._cursor) + this._buf.slice(this._cursor + 1);
    this._render();
  }

  _left() {
    if (this._cursor > 0) {
      this._cursor--;
      this._render();
    }
  }
  _right() {
    if (this._cursor < this._buf.length) {
      this._cursor++;
      this._render();
    }
  }
  _home() {
    this._cursor = 0;
    this._render();
  }
  _end() {
    this._cursor = this._buf.length;
    this._render();
  }

  _killToStart() {
    this._buf = this._buf.slice(this._cursor);
    this._cursor = 0;
    this._render();
  }
  _killToEnd() {
    this._buf = this._buf.slice(0, this._cursor);
    this._render();
  }

  _deleteWord() {
    // delete trailing whitespace then the word before the cursor
    let i = this._cursor;
    while (i > 0 && /\s/.test(this._buf[i - 1])) i--;
    while (i > 0 && !/\s/.test(this._buf[i - 1])) i--;
    this._buf = this._buf.slice(0, i) + this._buf.slice(this._cursor);
    this._cursor = i;
    this._render();
  }

  // Ctrl+C clears the line — and, mid-block, the VM's accumulated buffer too.
  // Clearing only this side would leave the prompt looking fresh while the next
  // thing typed got appended to a `defmodule` the visitor thought they escaped.
  _cancel() {
    this.term.write("^C");
    this._buf = "";
    this._cursor = 0;
    this._hi = -1;
    this._pasteQueue = [];
    this._pasteTail = null;
    this._newline();
    if (this._cont) {
      this._cont = false;
      this.onSubmit("#iex:break");
    }
    this._paint();
  }

  _clearScreen() {
    // clear scrollback + viewport, then repaint the current line
    this.term.write(ESC + "[2J" + ESC + "[3J" + ESC + "[H");
    this._paint();
  }

  _enter() {
    const line = this._buf;
    this._newline();
    if (line.trim()) {
      this._hist.push(line);
      // mirror into the hidden compat log so CDP harnesses see the command echo
    }
    this._hi = -1;
    this._stash = "";
    this._buf = "";
    this._cursor = 0;
    this._renderedCursor = 0;
    if (!line.trim() && !this._cont) {
      // empty line: just a fresh prompt, no eval
      this._paint();
      return;
    }
    // Inside a block a blank line is CONTENT, not a no-op: it is legal between a
    // module's functions and significant inside a heredoc. Swallowing it here
    // would silently rewrite the visitor's source.
    this._pending = true;
    this.onSubmit(line);
  }

  _histPrev() {
    if (this._hist.length === 0) return;
    if (this._hi === -1) {
      this._stash = this._buf;
      this._hi = this._hist.length;
    }
    if (this._hi > 0) {
      this._hi--;
      this._setLine(this._hist[this._hi]);
    }
  }

  _histNext() {
    if (this._hi === -1) return;
    if (this._hi < this._hist.length - 1) {
      this._hi++;
      this._setLine(this._hist[this._hi]);
    } else {
      this._hi = -1;
      this._setLine(this._stash);
    }
  }

  _setLine(s) {
    this._buf = s;
    this._cursor = s.length;
    this._render();
  }

  // ---- rendering (wrapped-line aware) ------------------------------------

  // Column/row of a character offset, modelling xterm's deferred wrap (a char in
  // the last column keeps the cursor there until the next char wraps it).
  _offToRC(offset, cols) {
    let row = 0;
    let col = 0;
    for (let i = 0; i < offset; i++) {
      col++;
      if (col > cols) {
        col = 1;
        row++;
      }
    }
    return { row, col };
  }

  // Clear the current input region back to the prompt origin, then repaint.
  _render() {
    const cols = (this.term && this.term.cols) || 80;
    this._promptLen = this._plainPrompt().length;
    const prevOff = this._promptLen + this._renderedCursor;
    const { row } = this._offToRC(prevOff, cols);
    let s = "\r";
    if (row > 0) s += ESC + "[" + row + "A";
    s += ESC + "[0J"; // erase from cursor to end of screen
    this.term.write(s);
    this._paint();
  }

  // Paint prompt + buffer at the current line origin (cursor assumed at col 0),
  // then place the physical cursor at the logical cursor.
  _paint() {
    const cols = (this.term && this.term.cols) || 80;
    this._promptLen = this._plainPrompt().length;
    this.term.write(this._ansiPrompt() + this._buf);
    const endOff = this._promptLen + this._buf.length;
    const curOff = this._promptLen + this._cursor;
    const end = this._offToRC(endOff, cols);
    const cur = this._offToRC(curOff, cols);
    let s = "";
    const up = end.row - cur.row;
    if (up > 0) s += ESC + "[" + up + "A";
    else if (up < 0) s += ESC + "[" + -up + "B";
    s += "\r";
    if (cur.col > 0) s += ESC + "[" + cur.col + "C";
    this.term.write(s);
    this._renderedCursor = this._cursor;
  }

  _newline() {
    this.term.write("\r\n");
  }

  // ---- output from Elixir ------------------------------------------------

  // First prompt after join (no output above it).
  writePrompt() {
    if (!this.term) return;
    this._buf = "";
    this._cursor = 0;
    this._renderedCursor = 0;
    this._pending = false;
    this._paint();
  }

  // An eval result: print each line (red when isError), then a fresh prompt.
  writeOutput(text, isError) {
    if (!this.term) return;
    this._cont = false;
    const color = isError ? RED : "";
    const reset = isError ? RESET : "";
    String(text == null ? "" : text)
      .split("\n")
      .forEach((ln) => this.term.write(color + ln + reset + "\r\n"));
    this._pending = false;
    this._buf = "";
    this._cursor = 0;
    this._renderedCursor = 0;
    this._paint();
    this.term.scrollToBottom();
    this._drainPaste();
  }

  // The VM says the submission so far does not parse yet (an unclosed `do`,
  // heredoc, bracket…). Draw a continuation prompt and keep collecting. This is
  // also what unfreezes the readline, so it has to arrive for EVERY incomplete
  // submission — see the `M` frame in WebOTP.Repl's moduledoc.
  writeContinuation() {
    if (!this.term) {
      this._cont = true;
      this._pending = false;
      return this._drainPaste();
    }
    this._cont = true;
    this._pending = false;
    this._buf = "";
    this._cursor = 0;
    this._renderedCursor = 0;
    this._paint();
    this.term.scrollToBottom();
    this._drainPaste();
  }

  // Raw IO output from the shell's group-leader io_server (BrowserNode.TermIO):
  // IO.puts / IO.write / IO.inspect / :io.put_chars. Streams put_chars bytes
  // verbatim (only \n -> \r\n for the terminal). During an in-progress eval the
  // readline is frozen (`_pending`) and no prompt is shown, so we stream inline
  // exactly like a real REPL. Output arriving while idle (a background/remote
  // process writing to our terminal — e.g. a peer's Node.spawn) is unsolicited,
  // so we drop to a clean line and repaint the prompt+input afterward, like a
  // wave. Distinct from writeLine (which is always line-oriented + colored).
  writeRaw(text) {
    if (!this.term) return;
    const raw = String(text == null ? "" : text);
    const crlf = raw.replace(/\r?\n/g, "\r\n");
    if (this._pending) {
      this.term.write(crlf);
    } else {
      this.term.write("\r" + ESC + "[0J");
      this.term.write(crlf);
      if (!raw.endsWith("\n")) this.term.write("\r\n");
      this._paint();
    }
    this.term.scrollToBottom();
  }

  // An unsolicited line (e.g. a peer wave). Keep the in-progress input intact.
  writeLine(text, color) {
    if (!this.term) return;
    const c = color === "red" ? RED : color === "cyan" ? CYAN : color === "dim" ? DIM : "";
    const reset = c ? RESET : "";
    // drop to a clean line, print, then repaint the current prompt+buffer
    this.term.write("\r" + ESC + "[0J");
    String(text).split("\n").forEach((ln) => this.term.write(c + ln + reset + "\r\n"));
    this._paint();
    this.term.scrollToBottom();
  }

  // Completion candidates: print the list, then repaint the current line so the
  // user's in-progress input (and cursor) is preserved.
  writeCompletions(items) {
    if (!this.term) return;
    const list = Array.isArray(items) ? items : [];
    if (list.length === 0) {
      return; // nothing to show; leave the line untouched
    }
    if (list.length === 1) {
      // single candidate: complete the trailing token in place
      this._applyCompletion(list[0]);
      return;
    }
    this._newline();
    this.term.write(DIM + list.join("   ") + RESET + "\r\n");
    this._paint();
    this.term.scrollToBottom();
  }

  // Extend the current trailing token to a single candidate (e.g. "Enu" -> "Enum").
  _applyCompletion(cand) {
    const cand0 = String(cand).replace(/\/\d+$/, ""); // strip "/arity"
    const before = this._buf.slice(0, this._cursor);
    const m = before.match(/([A-Za-z0-9_?!]*)$/);
    const frag = m ? m[1] : "";
    const dot = frag.lastIndexOf(".");
    const tail = dot === -1 ? frag : frag.slice(dot + 1);
    if (cand0.startsWith(tail)) {
      const add = cand0.slice(tail.length);
      if (add) this._insert(add);
      else {
        this._newline();
        this.term.write(DIM + cand + RESET + "\r\n");
        this._paint();
      }
    } else {
      this._newline();
      this.term.write(DIM + cand + RESET + "\r\n");
      this._paint();
    }
  }

  // Programmatic submit — used by the hidden #repl-run compat button so the
  // existing CDP harnesses (tab_eval.py, vl4.py, l5_*.py) drive the same path.
  submit(line) {
    const text = String(line);
    // A multi-line submit is a paste that ends in a newline, so every line goes
    // and nothing is left sitting in the input. Setting `_buf` to a string with
    // newlines in it would break every cursor calculation in `_paint`.
    const multi = /[\r\n]/.test(text);
    if (!this.term) {
      // terminal not mounted (headless probe before join): still fire onSubmit
      const lines = multi ? text.replace(/\r\n?/g, "\n").split("\n") : [text];
      for (const l of lines) {
        this._pending = true;
        this.onSubmit(l);
      }
      return;
    }
    if (multi) return this._paste(text.replace(/\r\n?/g, "\n") + "\n");
    this._buf = text;
    this._cursor = this._buf.length;
    this._render();
    this._enter();
  }

  // Populate the current input line WITHOUT submitting (sidebar peer-tap, tour
  // hints). Focuses the terminal so Enter sends it. No-op if frozen mid-eval.
  setInput(text) {
    if (!this.term || this._pending) return;
    this.term.focus();
    this._hi = -1;
    this._stash = "";
    this._setLine(String(text));
    this.term.scrollToBottom();
  }

  // Visible buffer text — real rendered content, for CDP verification.
  dump() {
    if (!this.term) return "";
    const b = this.term.buffer.active;
    const out = [];
    for (let i = 0; i < b.length; i++) {
      const line = b.getLine(i);
      if (line) out.push(line.translateToString(true));
    }
    return out.join("\n").replace(/\n+$/, "");
  }
}

const PopcornTerm = new PopcornTerminal();
if (typeof window !== "undefined") window.PopcornTerm = PopcornTerm;
export default PopcornTerm;
